/**
 * PlotlyChartViewer - Renders Plotly charts from JIRA ui_events
 * Supports all Plotly chart types (pie, bar, line, scatter, heatmap,
 * histogram, box, violin, waterfall, treemap, sunburst, funnel, etc.)
 * Automatically adapts to light/dark theme.
 */

import React from 'react';
// @ts-ignore plotly.js-dist-min ships its own typings via @types/plotly.js
import Plotly from 'plotly.js-dist-min';
// @ts-ignore factory export is a JS-only default export
import createPlotlyComponent from 'react-plotly.js/factory';
import { useTheme } from '../../Theme/ThemeProvider';
import { useDeviceDetection } from '../../../hooks/useDeviceDetection';

const Plot = createPlotlyComponent(Plotly);

export interface PlotlyPayload {
  data: Plotly.Data[];
  layout?: Partial<Plotly.Layout>;
  config?: Partial<Plotly.Config>;
}

interface PlotlyChartViewerProps {
  payload: PlotlyPayload;
  onImageCaptured?: (dataUrl: string) => void;
}

/** Accessible color palette aligned with design standards */
const CHART_PALETTE = [
  '#3850B7', // Blue
  '#3BC1EB', // Orange
  '#9657D9', // Purple
  '#65A603', // Green
  '#037FA8', // Pink
  '#FF9945', // Teal
  '#D474BA', // Red
  '#092B70'  // Yellow
];

/** Chart families for branching logic */
const PIE_FAMILY   = new Set(['pie', 'sunburst', 'treemap', 'icicle']);
const BAR_FAMILY   = new Set(['bar', 'histogram', 'waterfall', 'funnel', 'funnelarea']);
const LINE_FAMILY  = new Set(['scatter', 'scattergl', 'scatter3d', 'scatterpolar', 'scattergeo', 'line']);
const BOX_FAMILY   = new Set(['box', 'violin']);
const HEAT_FAMILY  = new Set(['heatmap', 'heatmapgl', 'contour', 'surface']);

function getFamily(type: string) {
  if (PIE_FAMILY.has(type))  return 'pie';
  if (BAR_FAMILY.has(type))  return 'bar';
  if (LINE_FAMILY.has(type)) return 'line';
  if (BOX_FAMILY.has(type))  return 'box';
  if (HEAT_FAMILY.has(type)) return 'heat';
  return 'other';
}

/**
 * Normalise trace types that the backend may emit but Plotly doesn't natively support.
 * e.g. chart_type:"line" → Plotly type:"scatter" mode:"lines+markers"
 */
function normaliseTrace(trace: any): any {
  if (trace.type === 'line') {
    return { mode: 'lines+markers', ...trace, type: 'scatter' };
  }
  return trace;
}

/** Apply palette and sensible defaults to every trace by chart family */

/**
 * Truncate long legend labels with an ellipsis.
 * @param text   Original trace name
 * @param maxLen Maximum characters before truncating (default 28)
 */
function truncateLegendText(text: string, maxLen = 220): string {
  if (!text || text.length <= maxLen) return text;
  return text.slice(0, maxLen - 1).trimEnd() + '…';
}

function applyPalette(data: Plotly.Data[]): Plotly.Data[] {
  return data.map((raw: any, i) => {
    const trace  = normaliseTrace(raw);
    const type   = trace.type ?? 'scatter';
    const family = getFamily(type);
    const color  = CHART_PALETTE[i % CHART_PALETTE.length];

    // Truncate long legend labels / pie slice labels with ellipsis
    if (trace.name) {
      trace.name = truncateLegendText(trace.name);
    }

    const hoverStyle = {
      bgcolor: '#313136',
      bordercolor: '#313136',
      font: { color: '#e5e7eb', size: 13 },
    };

    switch (family) {
      case 'pie': {
        const existingColors = trace.marker?.colors ?? [];
        // Also truncate pie slice labels shown in the legend
        const truncatedLabels = Array.isArray(trace.labels)
          ? (trace.labels as string[]).map((l: string) => truncateLegendText(l))
          : trace.labels;
        return {
          ...trace,
          labels: truncatedLabels,
          textinfo: trace.textinfo ?? 'percent',
          textposition: trace.textposition ?? 'auto',
          insidetextorientation: trace.insidetextorientation ?? 'radial',
          automargin: true,
          hole: trace.hole ?? 0,
          marker: {
            ...trace.marker,
            colors: existingColors.length ? existingColors : CHART_PALETTE,
            line: { color: '#ffffff', width: 2, ...(trace.marker?.line ?? {}) },
          },
          hoverlabel: hoverStyle,
        };
      }

      case 'bar': {
        // Each series gets its own palette color; individual bar colours preserved if set
        const hasExplicitColor = Array.isArray(trace.marker?.color)
          ? trace.marker.color.length > 0
          : !!trace.marker?.color;
        return {
          ...trace,
          marker: {
            ...trace.marker,
            color: hasExplicitColor ? trace.marker.color : color,
            opacity: trace.marker?.opacity ?? 0.88,
            line: { width: 0, ...(trace.marker?.line ?? {}) },
          },
          hoverlabel: hoverStyle,
        };
      }

      case 'line': {
        const hasExplicitColor = !!trace.line?.color || !!trace.marker?.color;
        return {
          ...trace,
          mode: trace.mode ?? 'lines+markers',
          line: {
            width: 2.5,
            shape: trace.line?.shape ?? 'linear',
            ...trace.line,
            color: hasExplicitColor ? trace.line?.color : color,
          },
          marker: {
            size: 6,
            ...trace.marker,
            color: hasExplicitColor ? trace.marker?.color : color,
          },
          hoverlabel: hoverStyle,
        };
      }

      case 'box': {
        return {
          ...trace,
          marker: { color, ...trace.marker },
          line:   { color, ...trace.line   },
          hoverlabel: hoverStyle,
        };
      }

      case 'heat': {
        // Heatmaps use colorscales don't override with palette
        return {
          ...trace,
          colorscale: trace.colorscale ?? 'Blues',
          hoverlabel: hoverStyle,
        };
      }

      default:
        return { ...trace, hoverlabel: hoverStyle };
    }
  });
}

/**
 * Detects if the chart data contains any pie-family traces.
 */
function hasPieFamily(data: Plotly.Data[]): boolean {
  return data.some((d: any) => PIE_FAMILY.has(d.type ?? ''));
}

/**
 * Changes layout by changing the font/text colors to dark values without changing backgrounds or structure
 * Used for export so that the resulting image is clear and legible regardless of the user's theme, while still respecting layout customizations like axis titles and gridlines.
 */
function buildExportFontOverrides(
  layout: Partial<Plotly.Layout> | undefined,
  isPie: boolean,
  data: Plotly.Data[] = []
): Partial<Plotly.Layout> {
  const lightLayout = buildThemedLayout(layout, false /* light */, isPie, data, false /* not mobile — exports are always full-size */);
  // Extract only the color-related fields from the light layout since both dark and light mode export as light
  return {
    font:   lightLayout.font,
    xaxis:  lightLayout.xaxis,
    yaxis:  lightLayout.yaxis,
    xaxis2: lightLayout.xaxis2,
    yaxis2: lightLayout.yaxis2,
    legend: lightLayout.legend,
    hoverlabel: lightLayout.hoverlabel,
    // Always export with transparent backgrounds regardless of display theme
    plot_bgcolor:  'rgba(0,0,0,0)',
    paper_bgcolor: 'rgba(0,0,0,0)',
  };
}

function buildThemedLayout(
  layout: Partial<Plotly.Layout> | undefined,
  isDark: boolean,
  isPie: boolean,
  data: Plotly.Data[] = [],
  isMobile: boolean = false
): Partial<Plotly.Layout> {
  const bg        = isDark ? '#313136' : '#ffffff';
  const paperBg   = isDark ? '#313136' : '#ffffff';
  const fontColor = isDark ? '#e5e7eb' : '#111827';
  const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const lineColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)';

  // Horizontal bar charts have categories on y-axis — need much more left margin
  const isHorizontalBar = data.some((d: any) => BAR_FAMILY.has(d.type ?? 'scatter') && d.orientation === 'h');

  // Smaller font sizes on mobile to keep axis labels legible without overflow
  const baseFontSize  = isMobile ? 10 : 13;
  const tickFontSize  = isMobile ? 9  : 12;
  const axisTitleSize = isMobile ? 9  : 12;

  const axisBase = {
    gridcolor:     gridColor,
    linecolor:     lineColor,
    zerolinecolor: lineColor,
    tickfont: { color: fontColor, size: tickFontSize },
    title:    { font: { color: fontColor, size: axisTitleSize } },
  };

  return {
    ...layout,
    template:      undefined,
    plot_bgcolor:  bg,
    paper_bgcolor: paperBg,
    // Show Plotly's built-in legend so it is included in the downloaded image
    showlegend:    false, // We render our own HTML legend below to avoid SVG clipping
    font: {
      family: '3MCircularTT, Inter, system-ui, sans-serif',
      color:  fontColor,
      size:   baseFontSize,
      ...(layout?.font ?? {}),
    },
    margin: {
      // Backend margin values intentionally NOT spread last — we control spacing
      l: isPie ? (isMobile ? 24 : 40) : isHorizontalBar ? (isMobile ? 120 : 180) : (isMobile ? 48 : 80),
      r: isPie ? (isMobile ? 24 : 40) : (isMobile ? 12 : 24),
      t: isPie ? (isMobile ? 24 : 40) : (isMobile ? 28 : 48),
      b: isPie ? (isMobile ? 24 : 40) : (isMobile ? 40 : 60),
    },
    xaxis: {
      ...axisBase,
      automargin: true,
      tickangle: -45,
      ...(layout?.xaxis ?? {}),
      // title applied AFTER backend spread so our standoff always wins
      title: {
        font:     { color: fontColor, family: '3MCircularTT, Inter, system-ui, sans-serif', size: axisTitleSize },
        standoff: isMobile ? 14 : 28,
        ...(typeof layout?.xaxis?.title === 'object'
          ? layout.xaxis.title
          : { text: layout?.xaxis?.title }),
      },
    },
    yaxis: {
      ...axisBase,
      automargin: true,
      ...(layout?.yaxis ?? {}),
      // title applied AFTER backend spread so our standoff always wins
      title: {
        font:     { color: fontColor, family: '3MCircularTT, Inter, system-ui, sans-serif', size: axisTitleSize },
        standoff: isHorizontalBar ? (isMobile ? 50 : 80) : (isMobile ? 14 : 28),
        ...(typeof layout?.yaxis?.title === 'object'
          ? layout.yaxis.title
          : { text: layout?.yaxis?.title }),
      },
    },
    xaxis2: { ...axisBase, ...(layout?.xaxis2 ?? {}) },
    yaxis2: { ...axisBase, ...(layout?.yaxis2 ?? {}) },
    legend: isPie
      ? {
          // Pie charts: legend below
          orientation: 'h',
          x:           0.5,
          xanchor:     'center',
          y:           -0.1,
          yanchor:     'top',
          font:        { color: fontColor, size: 12 },
          bgcolor:     isDark ? 'rgba(40,40,44,0.75)' : 'rgba(255,255,255,0.9)',
          bordercolor: lineColor,
          borderwidth: 1,
          ...(layout?.legend ?? {}),
        }
      : {
          // All other charts: legend on the right to avoid y-axis title collision
          orientation: 'v',
          x:           1.02,
          xanchor:     'left',
          y:           1,
          yanchor:     'top',
          font:        { color: fontColor, size: 12 },
          bgcolor:     isDark ? 'rgba(40,40,44,0.75)' : 'rgba(255,255,255,0.9)',
          bordercolor: lineColor,
          borderwidth: 1,
          entrywidthmode: 'pixels',
          entrywidth: 120,
          ...(layout?.legend ?? {}),
        },
    hoverlabel: {
      bgcolor:     '#313136',
      bordercolor: '#313136',
      font: { color: '#e5e7eb', size: 13 },
    },
    modebar: {
      bgcolor: 'transparent',
    },
    // Pass through any extra layout keys (geo, polar, ternary, etc.)
  };
}

function buildLegendItems(
  styledData: Plotly.Data[],
  isPie: boolean
): { label: string; color: string }[] {
  if (isPie) {
    const idx = styledData.findIndex((d: any) => PIE_FAMILY.has(d.type ?? ''));
    if (idx === -1) return [];
    const styled = styledData[idx] as any;
    const labels: string[] = styled.labels ?? (styled as any).ids ?? [];
    const colors: string[] = styled.marker?.colors ?? CHART_PALETTE;
    return labels.map((label: string, i: number) => ({
      label,
      color: colors[i % colors.length],
    }));
  }
  // For non-pie, each trace that has a name is a legend item
  return styledData
    .filter((d: any) => d.name && d.name.trim() !== '')
    .map((d: any, i: number) => {
      // Remove any <br> text wrapping for the HTML legend
      const rawName = (d.name as string).replace(/<br>/g, ' ');
      const color =
        d.line?.color ??
        (Array.isArray(d.marker?.color) ? d.marker.color[0] : d.marker?.color) ??
        CHART_PALETTE[i % CHART_PALETTE.length];
      return { label: rawName, color: color as string };
    });
}

/**
 * Paint a legend strip below the chart PNG and resolve with the composite data-URL.
 * This is used both for DOCX capture (handlePlotUpdate) and the custom toolbar button.
 */
function compositeLegend(
  dataUrl: string,
  items: { label: string; color: string }[],
  imgWidth: number,
  imgHeight: number,
): Promise<string> {
  if (items.length === 0) return Promise.resolve(dataUrl);

  const LEGEND_H  = 40;
  const SWATCH    = 12;
  const GAP       = 6;
  const ITEM_GAP  = 24;
  const FONT_SIZE = 13;
  const FONT_FACE = 'Arial, sans-serif';

  return new Promise((resolve) => {
    const canvas  = document.createElement('canvas');
    canvas.width  = imgWidth;
    canvas.height = imgHeight + LEGEND_H;
    const ctx = canvas.getContext('2d')!;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, imgWidth, imgHeight);

      // Legend strip
      ctx.fillStyle = '#f9fafb';
      ctx.fillRect(0, imgHeight, imgWidth, LEGEND_H);
      ctx.strokeStyle = 'rgba(0,0,0,0.08)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, imgHeight);
      ctx.lineTo(imgWidth, imgHeight);
      ctx.stroke();

      // Measure & center items
      ctx.font = `${FONT_SIZE}px ${FONT_FACE}`;
      const itemWidths = items.map(({ label }) => SWATCH + GAP + ctx.measureText(label).width);
      const totalW = itemWidths.reduce((a, b) => a + b, 0) + ITEM_GAP * (items.length - 1);
      let x = Math.max(16, (imgWidth - totalW) / 2);
      const swatchY = imgHeight + (LEGEND_H - SWATCH) / 2;
      const textY   = swatchY + SWATCH - 1;

      items.forEach(({ label, color }, i) => {
        ctx.fillStyle = color;
        ctx.beginPath();
        (ctx as any).roundRect?.(x, swatchY, SWATCH, SWATCH, 2) ?? ctx.rect(x, swatchY, SWATCH, SWATCH);
        ctx.fill();
        ctx.fillStyle = '#374151';
        ctx.font = `${FONT_SIZE}px ${FONT_FACE}`;
        ctx.fillText(label, x + SWATCH + GAP, textY);
        x += itemWidths[i] + ITEM_GAP;
      });

      resolve(canvas.toDataURL('image/png'));
    };
    img.src = dataUrl;
  });
}

const PlotlyChartViewer: React.FC<PlotlyChartViewerProps> = ({ payload, onImageCaptured }) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const { isMobile } = useDeviceDetection();
  const hasCapturedRef = React.useRef(false);

  // We render our own HTML legend to avoid SVG clipping and support ellipsis.
  const isPie        = hasPieFamily(payload.data);
  const chartHeight  = isPie ? (isMobile ? 300 : 420) : (isMobile ? 260 : 360);

  const styledData    = applyPalette(payload.data);
  const themedLayout  = buildThemedLayout(payload.layout, isDark, isPie, payload.data, isMobile);
  const allLegendItems = buildLegendItems(styledData, isPie);

  // A single-series legend is always redundant — suppress it
  const legendItems = allLegendItems.length === 1 ? [] : allLegendItems;

  const handlePlotInitialized = React.useCallback((_figure: any, graphDiv: any) => {
    if (hasCapturedRef.current || !onImageCaptured || !graphDiv) return;
    hasCapturedRef.current = true;
    const isPieLocal = hasPieFamily(payload.data);
    const imgWidth  = 624;
    const imgHeight = isPieLocal ? 320 : 280;
    const styledD   = applyPalette(payload.data);
    const fontOverrides = buildExportFontOverrides(payload.layout, isPieLocal, payload.data);
    const exportFigure = {
      data:   styledD,
      layout: { ...graphDiv.layout, ...fontOverrides, height: isPieLocal ? 420 : 360 },
    };
    (Plotly as any).toImage(exportFigure, { format: 'png', width: imgWidth, height: imgHeight, scale: 1 })
      .then((dataUrl: string) => {
        const items = buildLegendItems(styledD, isPieLocal);
        return compositeLegend(dataUrl, items, imgWidth, imgHeight);
      })
      .then((finalUrl: string) => onImageCaptured(finalUrl))
      .catch(() => {});
  }, [onImageCaptured, payload.data, payload.layout]);

  // Always-fresh ref for the copy-to-clipboard modeBar button
  const copyToClipboardRef = React.useRef<(gd: any) => void>(() => {});
  copyToClipboardRef.current = (gd: any) => {
    const isPieLocal = hasPieFamily(payload.data);
    const imgWidth   = 900;
    const imgHeight  = isPieLocal ? 460 : 400;
    const fontOverrides = buildExportFontOverrides(payload.layout, isPieLocal, payload.data);
    const exportFigure = {
      data:   styledData,
      layout: { ...gd.layout, ...fontOverrides, height: isPieLocal ? 420 : 360 },
    };
    (Plotly as any).toImage(exportFigure, { format: 'png', width: imgWidth, height: imgHeight, scale: 1 })
      .then((dataUrl: string) => {
        const items = buildLegendItems(styledData, isPieLocal);
        return compositeLegend(dataUrl, items, imgWidth, imgHeight);
      })
      .then((finalUrl: string) => {
        // Convert data URL to blob and write to clipboard
        const base64 = finalUrl.split(',')[1];
        const byteChars = atob(base64);
        const byteArr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArr], { type: 'image/png' });
        return navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      })
      .catch(() => {});
  };

  // Always-fresh ref so the custom modeBar button captures latest payload/styledData
  const downloadWithLegendRef = React.useRef<(gd: any) => void>(() => {});
  downloadWithLegendRef.current = (gd: any) => {
    const isPieLocal = hasPieFamily(payload.data);
    const imgWidth   = 900;
    const imgHeight  = isPieLocal ? 460 : 400;
    const chartTitle = (payload.layout as any)?.title?.text
      ?? (typeof (payload.layout as any)?.title === 'string' ? (payload.layout as any).title : null)
      ?? 'chart';
    const fontOverrides = buildExportFontOverrides(payload.layout, isPieLocal, payload.data);
    const exportFigure = {
      data:   styledData,
      layout: { ...gd.layout, ...fontOverrides, height: isPieLocal ? 420 : 360 },
    };
    (Plotly as any).toImage(exportFigure, { format: 'png', width: imgWidth, height: imgHeight, scale: 1 })
      .then((dataUrl: string) => {
        const items = buildLegendItems(styledData, isPieLocal);
        return compositeLegend(dataUrl, items, imgWidth, imgHeight);
      })
      .then((finalUrl: string) => {
        const a = document.createElement('a');
        a.href = finalUrl;
        a.download = `${chartTitle}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      })
      .catch(() => {});
  };


  const config: Partial<Plotly.Config> = {
    responsive:     true,
    displayModeBar: true,
    displaylogo:    false,
    modeBarIconSize: isMobile ? 14 : 20,
    // Remove default camera button; our custom one composites the legend before downloading
    modeBarButtonsToRemove: ['sendDataToCloud', 'lasso2d', 'select2d', 'toImage'] as any,
    modeBarButtonsToAdd: [
      {
        name:  'Copy chart to clipboard',
        title: 'Copy chart as image',
        // BiCopy icon scaled from 24×24 → 1000×1000 to match Plotly icon space
        icon: {
          width:     1000,
          height:    1000,
          path:      'M833 83H417c-46 0 -83 37 -83 83v167H167c-46 0 -83 37 -83 83v417c0 46 37 83 83 83h417c46 0 83 -37 83 -83v-167h167c46 0 83 -37 83 -83V167c0 -46 -37 -83 -83 -83zM167 833V417h417l0.083 417H167zm667 -250h-167v-167c0 -46 -37 -83 -83 -83h-167V167h417v417z',
          transform: 'matrix(1 0 0 1 0 0)',
        },
        click: (gd: any) => copyToClipboardRef.current(gd),
      },
      {
        name:  'Download PNG with legend',
        title: 'Download as PNG (with legend)',
        icon:  {
          width:  1000,
          height: 1000,
          path:   'M500.03 69.99C516.57 69.99 530.00 83.43 530.00 99.99V561.57L678.79 412.79C690.51 401.07 709.50 401.07 721.21 412.79C732.93 424.50 732.93 443.50 721.21 455.21L521.22 655.21C509.50 666.93 490.51 666.93 478.79 655.21L278.79 455.21C267.07 443.50 267.07 424.50 278.79 412.79C290.51 401.07 309.51 401.07 321.22 412.79L470.03 561.57V99.99C470.03 83.43 483.47 69.99 500.03 69.99ZM166.67 666.67C185.08 666.67 200.00 681.59 200.00 700.00V800.00C200.00 836.93 229.71 866.67 264.42 866.67H733.47C734.86 866.67 800.00 836.85 800.00 800.00V700.00C800.00 681.59 814.93 666.67 833.33 666.67C851.74 666.67 866.67 681.59 866.67 700.00V800.00C866.67 873.61 804.13 933.33 733.47 933.33H264.42C193.35 933.33 133.33 873.53 133.33 800.00V700.00C133.33 681.59 148.26 666.67 166.67 666.67Z',
          transform: 'matrix(1 0 0 1 0 0)',
        },
        click: (gd: any) => downloadWithLegendRef.current(gd),
      },
    ] as any,
    ...payload.config,
  };

  const title = (payload.layout as any)?.title?.text
    ?? (typeof (payload.layout as any)?.title === 'string' ? (payload.layout as any).title : null);

  return (
    <div
      style={{
        marginTop:    '1.25rem',
        marginBottom: '1.25rem',
        overflow: 'visible',
        width: '100%',
        background:   isDark ? '#313136' : '#ffffff',
        borderRadius: '8px',
      }}
    >
      {/* Title bar */}
      {title && (
        <div
          style={{
            padding:      '0.75rem 1rem 0.6rem',
            fontFamily:   '3MCircularTT, Inter, system-ui, sans-serif',
            fontWeight:   600,
            fontSize:     '0.95rem',
            color:        isDark ? '#e5e7eb' : '#111827',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
          }}
        >
          {title}
        </div>
      )}

      {/* Chart canvas */}
      <div style={{ padding: '0.25rem 0.5rem 0' }}>
        <Plot
          data={styledData}
          layout={{ ...themedLayout, height: chartHeight }}
          config={config}
          onInitialized={handlePlotInitialized}
          style={{ width: '100%', height: `${chartHeight}px`, display: 'block' }}
        />
      </div>

      {/* HTML legend — immune to SVG clipping, supports ellipsis */}
      {legendItems.length > 0 && (
        <div
          style={{
            display:        'flex',
            flexWrap:       'wrap',
            gap:            '6px 16px',
            padding:        '6px 12px 8px',
            borderTop:      `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
          }}
        >
          {legendItems.map(({ label, color }, idx) => (
            <div
              key={idx}
              style={{
                display:    'flex',
                alignItems: 'center',
                gap:        '5px',
                maxWidth:   '180px',
                minWidth:   0,
              }}
            >
              <span style={{
                flexShrink:   0,
                width:        '10px',
                height:       '10px',
                borderRadius: '2px',
                background:   color,
              }} />
              <span style={{
                fontFamily:   '3MCircularTT, Inter, system-ui, sans-serif',
                fontSize:     '0.75rem',
                color:        isDark ? '#d1d5db' : '#374151',
                overflow:     'hidden',
                textOverflow: 'ellipsis',
                whiteSpace:   'nowrap',
                minWidth:     0,
              }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlotlyChartViewer;
